import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { useMother } from '../lib/MotherContext';
import { Globe, Heart, Menu, X, User } from 'lucide-react';

const NAV_ITEMS = [
  { to: '/self-check', key: 'nav_self_check' },
  { to: '/library', key: 'nav_library' },
  { to: '/about', key: 'nav_about' },
  { to: '/milk-log', key: 'nav_milk_log' },
  { to: '/consultants', key: 'nav_consultants' },
  { to: '/booking', key: 'nav_booking' },
];

export default function Layout({ children }) {
  const { lang, setLang, t } = useLanguage();
  const { mother } = useMother();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-ivory" data-testid="app-layout">
      <header className="sticky top-0 z-40 bg-ivory/85 backdrop-blur-md border-b border-border/40">
        <div className="container-app flex items-center justify-between h-16">
          <Link to="/" data-testid="header-home-link" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-2xl bg-terracotta-light flex items-center justify-center">
              <Heart size={18} className="text-terracotta" strokeWidth={2.2} />
            </div>
            <div>
              <div className="font-heading font-semibold text-ink leading-tight text-sm sm:text-base">
                You & Me
              </div>
              <div className="text-[10px] sm:text-xs text-ink-tertiary leading-tight">
                {lang === 'zh' ? '哺乳教练' : 'Lactation Coach'}
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.key}`}
                className={`px-3 xl:px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  location.pathname === item.to
                    ? 'bg-terracotta-light/60 text-terracotta'
                    : 'text-ink-secondary hover:bg-subtle hover:text-ink'
                }`}
              >
                {t(item.key)}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to={mother ? '/my' : '/profile/login'}
              data-testid={mother ? 'nav-my-profile' : 'nav-profile-login'}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-terracotta-light/70 hover:bg-terracotta-light text-sm font-medium text-terracotta transition-colors"
            >
              <User size={14} />
              <span className="max-w-[120px] truncate">
                {mother ? (mother.name || t('nav_my')) : t('profile_login_link')}
              </span>
            </Link>

            <button
              onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')}
              data-testid="language-toggle"
              className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-sage-light hover:bg-[#D5DDD2] text-sm font-medium text-ink transition-colors"
            >
              <Globe size={14} />
              {lang === 'zh' ? 'EN' : '中'}
            </button>

            <button
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 rounded-full hover:bg-subtle"
              aria-label={t('nav_menu')}
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile menu drawer */}
        {mobileOpen && (
          <div
            className="lg:hidden border-t border-border/40 bg-ivory animate-fade-in-up"
            data-testid="mobile-menu"
          >
            <nav className="container-app py-4 grid gap-1">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMobile}
                  data-testid={`mobile-nav-${item.key}`}
                  className={`px-4 py-3 rounded-2xl text-sm font-medium transition-colors ${
                    location.pathname === item.to
                      ? 'bg-terracotta-light/60 text-terracotta'
                      : 'text-ink-secondary hover:bg-subtle hover:text-ink'
                  }`}
                >
                  {t(item.key)}
                </Link>
              ))}
              <Link
                to={mother ? '/my' : '/profile/login'}
                onClick={closeMobile}
                data-testid={mother ? 'mobile-nav-my' : 'mobile-nav-profile-login'}
                className="px-4 py-3 rounded-2xl text-sm font-medium text-terracotta hover:bg-terracotta-light/40 border-t border-border/40 mt-1 pt-3 inline-flex items-center gap-2"
              >
                <User size={14} /> {mother ? (mother.name || t('nav_my')) : t('profile_login_link')}
              </Link>
              <Link
                to="/admin"
                onClick={closeMobile}
                data-testid="mobile-nav-admin"
                className="px-4 py-3 rounded-2xl text-sm font-medium text-ink-tertiary hover:bg-subtle"
              >
                {t('nav_admin')}
              </Link>
            </nav>
          </div>
        )}
      </header>

      <main className="pb-20" data-testid="main-content">{children}</main>

      <footer className="bg-subtle border-t border-border/40 mt-12">
        <div className="container-app py-8">
          <div className="text-center text-xs text-ink-tertiary">
            <div className="font-medium text-ink-secondary mb-1">You & Me Lactation Coach</div>
            <div className="mb-4">
              {lang === 'zh'
                ? '本工具不取代医疗诊断 · 如需紧急照护请尽快就医'
                : 'This tool does not replace medical diagnosis · Please seek urgent care if needed'}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
              <Link to="/privacy" data-testid="footer-privacy" className="hover:text-terracotta">
                {t('nav_privacy')}
              </Link>
              <span className="text-ink-tertiary/40">·</span>
              <Link to="/terms" data-testid="footer-terms" className="hover:text-terracotta">
                {t('nav_terms')}
              </Link>
              <span className="text-ink-tertiary/40">·</span>
              <Link to="/about" data-testid="footer-about" className="hover:text-terracotta">
                {t('nav_about')}
              </Link>
              <span className="text-ink-tertiary/40">·</span>
              <Link to="/admin" data-testid="footer-admin" className="hover:text-terracotta opacity-60 hover:opacity-100">
                {t('nav_admin')}
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
